import React, { useEffect, useState } from 'react';
import { getStats } from '../api';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const COLORS = ['#7C3AED', '#E67E22', '#27AE60', '#E74C3C', '#9B59B6', '#1ABC9C', '#F39C12', '#3498DB'];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getStats().then(setStats).catch(console.error);
  }, []);

  if (!stats) return <div className="loading">Chargement du dashboard...</div>;

  const secteurData = {
    labels: stats.by_secteur.map(s => s.secteur),
    datasets: [{
      data: stats.by_secteur.map(s => parseInt(s.count)),
      backgroundColor: COLORS,
      borderWidth: 0,
    }],
  };

  const roundLabels = { 0: 'Pré-seed', 1: 'Seed', 2: 'Série A', 3: 'Série B', 4: 'Série C+' };
  const roundData = {
    labels: stats.by_round.map(r => roundLabels[r.round_levee] || `Round ${r.round_levee}`),
    datasets: [{
      label: 'Nombre de startups',
      data: stats.by_round.map(r => parseInt(r.count)),
      backgroundColor: '#7C3AED',
      borderRadius: 8,
    }],
  };

  const avgLabels = ['BP', 'Ratio', 'Solidité', 'Maturité', 'Marché', 'Innovation',
    'Équipe', 'Org. Comm.', 'Notoriété', 'RSE', 'RGPD', 'Conformité'];
  const avgKeys = ['avg_bp', 'avg_ratio', 'avg_solidite', 'avg_maturite', 'avg_marche', 'avg_innovation',
    'avg_equipe', 'avg_org_comm', 'avg_notoriete', 'avg_rse', 'avg_rgpd', 'avg_conformite'];
  const avgData = {
    labels: avgLabels,
    datasets: [{
      label: 'Moyenne /10',
      data: avgKeys.map(k => parseFloat(stats.averages[k]) || 0),
      backgroundColor: avgKeys.map(k => {
        const v = parseFloat(stats.averages[k]) || 0;
        return v > 7 ? '#27AE60' : v >= 4 ? '#F39C12' : '#E74C3C';
      }),
      borderRadius: 6,
    }],
  };

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <h1 style={{ marginBottom: 32 }}>Dashboard Analytique</h1>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Startups évaluées</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--primary)' }}>{stats.averages.avg_sectorielle}</div>
            <div className="stat-label">Note ISI moyenne</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#27AE60' }}>
              {stats.by_risk?.find(r => r.risk_level === 'vert')?.count || 0}
            </div>
            <div className="stat-label">Résilientes (vert)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#F39C12' }}>
              {stats.by_risk?.find(r => r.risk_level === 'orange')?.count || 0}
            </div>
            <div className="stat-label">Vigilance (orange)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#E74C3C' }}>
              {stats.by_risk?.find(r => r.risk_level === 'rouge')?.count || 0}
            </div>
            <div className="stat-label">Critiques (rouge)</div>
          </div>
        </div>

        <div className="detail-grid" style={{ marginBottom: 32 }}>
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Répartition par secteur</h3>
            <div style={{ maxWidth: 350, margin: '0 auto' }}>
              <Doughnut data={secteurData} options={{
                plugins: { legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true } } },
                cutout: '55%',
              }} />
            </div>
          </div>
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Répartition par round de levée</h3>
            <Bar data={roundData} options={{
              responsive: true,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, ticks: { stepSize: 5 } } },
            }} />
          </div>
        </div>

        <div className="card" style={{ marginBottom: 32 }}>
          <h3 style={{ marginBottom: 16 }}>Moyennes par cluster (ensemble de l'échantillon)</h3>
          <Bar data={avgData} options={{
            responsive: true,
            plugins: { legend: { display: false } },
            scales: {
              y: { min: 0, max: 10, ticks: { stepSize: 2 } },
              x: { ticks: { font: { size: 11 } } },
            },
          }} />
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Top 5 Startups</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: 12, textAlign: 'left' }}>#</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Startup</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Secteur</th>
                <th style={{ padding: 12, textAlign: 'center' }}>Note ISI</th>
              </tr>
            </thead>
            <tbody>
              {stats.top5.map((s, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: 12, fontWeight: 700, color: i === 0 ? 'var(--accent)' : 'inherit' }}>{i + 1}</td>
                  <td style={{ padding: 12, fontWeight: 600 }}>{s.name}</td>
                  <td style={{ padding: 12 }}><span className="badge badge-secteur">{s.categorie}</span></td>
                  <td style={{ padding: 12, textAlign: 'center', fontWeight: 700,
                    color: parseFloat(s.note_sectorielle) > 7 ? '#27AE60' : '#F39C12' }}>
                    {parseFloat(s.note_sectorielle).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
